import React, { useState } from "react";
import PageLoader from "components/ui/PageLoader";
import { FiChevronRight, FiFolder } from "react-icons/fi";
import BrandAssetsBrowser from "components/brandAssets/BrandAssetsBrowser";
import SystemTemplatesBrowser from "components/brandAssets/SystemTemplatesBrowser";
import ArtworkConversionsBrowser from "components/brandAssets/ArtworkConversionsBrowser";
import {
  useGetBrandAssetLibraryQuery,
  useSaveBrandAssetsMutation,
} from "apis/templateLibrary";
import {
  brandAssetUploadFolder,
  groupBrandAssetUploadFolder,
} from "utils/network";

// The brand assets on each network and group, browsed as folders.

const fileCount = (brandAssets = []) =>
  brandAssets.reduce((total, asset) => total + (asset.files?.length || 0), 0);

const Tile = ({ label, hint, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex items-center gap-3 rounded-lg border border-mediumgray bg-white px-3.5 py-3 text-left transition-colors hover:border-darkgray hover:bg-lightgray"
  >
    <FiFolder className="h-5 w-5 flex-shrink-0 text-darkgray" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] font-medium text-black">{label}</div>
      <div className="truncate text-[11px] text-darkgray">{hint}</div>
    </div>
  </button>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
    {children}
  </div>
);

const Crumbs = ({ trail }) => (
  <div className="flex flex-wrap items-center gap-1">
    {trail.map((crumb, index) => (
      <React.Fragment key={crumb.label}>
        {index > 0 ? (
          <FiChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-darkgray" />
        ) : null}
        {crumb.onClick ? (
          <button
            type="button"
            onClick={crumb.onClick}
            className="rounded px-1.5 py-1 text-[13px] text-darkgray hover:bg-lightgray hover:text-black"
          >
            {crumb.label}
          </button>
        ) : (
          <span className="truncate px-1.5 text-[13px] font-medium text-black">
            {crumb.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const TemplateLibrary = () => {
  const [networkId, setNetworkId] = useState(null);
  const [section, setSection] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const { data, isPending, isError, error } = useGetBrandAssetLibraryQuery();
  const { mutate: saveAssets, isPending: isSaving } =
    useSaveBrandAssetsMutation();

  if (isPending) return <PageLoader />;

  if (isError) {
    return (
      <div className="container mx-auto py-8 text-center">
        <p className="text-red-600">
          Error loading brand assets: {error?.message}
        </p>
      </div>
    );
  }

  const network = data.find((entry) => entry._id === networkId);
  const group = network?.groups.find((entry) => entry._id === groupId);

  const openNetwork = () => {
    setSection(null);
    setGroupId(null);
  };

  const trail = [
    {
      label: "Template library",
      onClick: network ? () => setNetworkId(null) : undefined,
    },
    ...(network
      ? [
          {
            label: network.name || network.slug || "Network",
            onClick: section ? openNetwork : undefined,
          },
        ]
      : []),
    ...(section === "network" ? [{ label: "Brand assets" }] : []),
    ...(section === "system" ? [{ label: "System templates" }] : []),
    ...(section === "conversions" ? [{ label: "Template drafts" }] : []),
    ...(section === "groups"
      ? [
          {
            label: "Groups",
            onClick: group ? () => setGroupId(null) : undefined,
          },
        ]
      : []),
    ...(group
      ? [{ label: group.groupName || group.code || "Group" }]
      : []),
  ];

  return (
    <div className="container mx-auto flex flex-col gap-4 py-1">
      <h1 className="text-lg font-medium text-black">Template Library</h1>
      <Crumbs trail={trail} />

      {!network ? (
        data.length === 0 ? (
          <p className="text-[13px] italic text-darkgray">No networks yet</p>
        ) : (
          <Grid>
            {data.map((entry) => (
              <Tile
                key={entry._id}
                label={entry.name || entry.slug || "Unnamed network"}
                hint={`${fileCount(entry.brandAssets)} files · ${entry.groups.length} groups`}
                onOpen={() => setNetworkId(entry._id)}
              />
            ))}
          </Grid>
        )
      ) : group ? (
        <BrandAssetsBrowser
          assets={group.brandAssets}
          folderFor={(category) =>
            groupBrandAssetUploadFolder(group._id, category)
          }
          onSave={(brandAssets) =>
            saveAssets({ level: "group", id: group._id, brandAssets })
          }
          saving={isSaving}
        />
      ) : section === "groups" ? (
        network.groups.length === 0 ? (
          <p className="text-[13px] italic text-darkgray">
            No groups under this network
          </p>
        ) : (
          <Grid>
            {network.groups.map((entry) => (
              <Tile
                key={entry._id}
                label={entry.groupName || entry.code || entry._id}
                hint={`${fileCount(entry.brandAssets)} files`}
                onOpen={() => setGroupId(entry._id)}
              />
            ))}
          </Grid>
        )
      ) : section === "network" ? (
        <BrandAssetsBrowser
          assets={network.brandAssets}
          folderFor={(category) =>
            brandAssetUploadFolder(network, network.slug, category)
          }
          onSave={(brandAssets) =>
            saveAssets({ level: "network", id: network._id, brandAssets })
          }
          saving={isSaving}
        />
      ) : section === "system" ? (
        <SystemTemplatesBrowser networkId={network._id} />
      ) : section === "conversions" ? (
        <ArtworkConversionsBrowser networkId={network._id} />
      ) : (
        <Grid>
          <Tile
            label="Brand assets"
            hint={`${fileCount(network.brandAssets)} files on the network`}
            onOpen={() => setSection("network")}
          />
          <Tile
            label="Groups"
            hint={`${network.groups.length} groups`}
            onOpen={() => setSection("groups")}
          />
          <Tile
            label="System templates"
            hint="Coded by Beleef — view sizes, download samples"
            onOpen={() => setSection("system")}
          />
          <Tile
            label="Template drafts"
            hint="Code drafted from uploaded artwork PDFs"
            onOpen={() => setSection("conversions")}
          />
        </Grid>
      )}
    </div>
  );
};

export default TemplateLibrary;
